/* ==========================================================================
   Wishlist – Storage + Web Components
   Loaded globally via theme.liquid
   ========================================================================== */

const WISHLIST_STORAGE_KEY = 'wishlist-items';

/* ---------- WishlistStorage ---------- */

class WishlistStorage {
  static getAll() {
    try {
      return JSON.parse(localStorage.getItem(WISHLIST_STORAGE_KEY)) || [];
    } catch {
      return [];
    }
  }

  static has(handle) {
    return this.getAll().some((item) => item.handle === handle);
  }

  static add(product) {
    const items = this.getAll().filter((item) => item.handle !== product.handle);
    items.push({ ...product, addedAt: Date.now() });
    localStorage.setItem(WISHLIST_STORAGE_KEY, JSON.stringify(items));
    this._publish();
    this._trackEvent('wishlist_add', product);
  }

  static remove(handle) {
    const removed = this.getAll().find((item) => item.handle === handle);
    const items = this.getAll().filter((item) => item.handle !== handle);
    localStorage.setItem(WISHLIST_STORAGE_KEY, JSON.stringify(items));
    this._publish();
    if (removed) this._trackEvent('wishlist_remove', removed);
  }

  static toggle(product) {
    if (this.has(product.handle)) {
      this.remove(product.handle);
      return false;
    }
    this.add(product);
    return true;
  }

  static getCount() {
    return this.getAll().length;
  }

  static _publish() {
    publish(PUB_SUB_EVENTS.wishlistUpdate, { count: this.getCount() });
  }

  static _trackEvent(eventName, product) {
    if (typeof gtag === 'function') {
      gtag('event', eventName, {
        item_id: product.handle,
        item_name: product.title,
        price: (product.price / 100).toFixed(2),
        send_to: 'G-CBCWSE3F4Q',
      });
    }
  }
}

/* ---------- <wishlist-button> ---------- */

class WishlistButton extends HTMLElement {
  constructor() {
    super();
    this.button = null;
  }

  connectedCallback() {
    this.button = this.querySelector('.wishlist-button');
    if (!this.button) return;

    this._syncState();
    this.button.addEventListener('click', this._onClick.bind(this));
    this._unsubscribe = subscribe(PUB_SUB_EVENTS.wishlistUpdate, this._syncState.bind(this));
  }

  disconnectedCallback() {
    if (this._unsubscribe) this._unsubscribe();
  }

  _getProductData() {
    return {
      handle: this.dataset.productHandle,
      id: Number(this.dataset.productId),
      title: this.dataset.productTitle,
      url: this.dataset.productUrl,
      price: Number(this.dataset.productPrice),
      compareAtPrice: this.dataset.productCompareAtPrice ? Number(this.dataset.productCompareAtPrice) : null,
      imageUrl: this.dataset.productImage,
      imageAlt: this.dataset.productImageAlt || '',
      available: this.dataset.productAvailable !== 'false',
    };
  }

  _syncState() {
    const active = WishlistStorage.has(this.dataset.productHandle);
    this.classList.toggle('is-active', active);
    const outlineIcon = this.querySelector('.icon-heart');
    const filledIcon = this.querySelector('.icon-heart-filled');
    if (outlineIcon) outlineIcon.style.display = active ? 'none' : '';
    if (filledIcon) filledIcon.style.display = active ? '' : 'none';
    if (this.button) {
      this.button.setAttribute('aria-pressed', String(active));
      this.button.setAttribute('aria-label', active ? 'Remove from wishlist' : 'Add to wishlist');
    }
  }

  _onClick(e) {
    e.preventDefault();
    e.stopPropagation();
    WishlistStorage.toggle(this._getProductData());
    this._syncState();
  }
}

if (!customElements.get('wishlist-button')) {
  customElements.define('wishlist-button', WishlistButton);
}

/* Expose for global access (script is loaded as a plain <script>, not a module) */
window.WishlistStorage = WishlistStorage;

/* ---------- <wishlist-header-icon> ---------- */

class WishlistHeaderIcon extends HTMLElement {
  connectedCallback() {
    this.bubble = this.querySelector('.wishlist-count-bubble');
    this.countEl = this.bubble ? this.bubble.querySelector('[data-wishlist-count]') : null;
    this._syncCount();
    subscribe(PUB_SUB_EVENTS.wishlistUpdate, this._syncCount.bind(this));
  }

  _syncCount() {
    const count = WishlistStorage.getCount();
    if (this.bubble) {
      this.bubble.hidden = count === 0;
    }
    if (this.countEl) {
      this.countEl.textContent = count;
    }
  }
}

if (!customElements.get('wishlist-header-icon')) {
  customElements.define('wishlist-header-icon', WishlistHeaderIcon);
}
